import JwtBaseClient from './JwtBaseClient';

class TransportGatewayClient {
  constructor() {
    this.jwtBaseClient = new JwtBaseClient(import.meta.env.VITE_TRANSPORT_MARKETPLACE_GATEWAY_BASE_URI, 'response.data');
    this.gatePassUri = '/api/v1/gate-pass/upload';
    this.decodeVinUri = '/api/v1/decode/vin';
    this.transportPromoUri = '/api/v1/transport-promo';
    this.searchRequestUri = '/api/v1/transport-request/filter';
    this.updateRequestUri = '/api/v1/transport-request';
    this.getOffPlatformTransportQuoteUri = '/api/v1/transport-quote';
    this.createOffPlatformBulkRequestsUri = '/api/v1/transport-request/off-platform/bulk';
    this.createBulkRequestsUri = '/api/v1/transport-request/bulk';
    this.checkoutOptions = '/api/v1/checkout-options';
    this.transportQuote = '/api/v1/transport-quote';
    this.createTransportRequest = '/api/v1/transport-request/off-platform/bulk';
    this.transportQuoteAuction = '/api/v1/transport-quote/auction';
    this.formDataHeader = {
      'Content-Type': 'multipart/form-data',
    };
  }

  uploadGatePass(payload) {
    return this.jwtBaseClient.post(this.gatePassUri, payload, this.formDataHeader);
  }

  addGatePassToRequest(jobId, gatePassFileKey) {
    const payload = {
      gatePassFileKey,
    };

    return this.jwtBaseClient.put(`${this.updateRequestUri}/${jobId}`, payload);
  }

  removeGatePass(jobId) {
    return this.jwtBaseClient.put(`${this.updateRequestUri}/${jobId}`, { gatePassFileKey: '' });
  }

  getDecodedVin(vin) {
    return this.jwtBaseClient.get(`${this.decodeVinUri}/${vin}`);
  }

  getOffPlatformTransportQuote(payload) {
    return this.jwtBaseClient.post(`${this.getOffPlatformTransportQuoteUri}`, payload);
  }

  createOffPlatformBulkRequests(payload) {
    return this.jwtBaseClient.post(`${this.createOffPlatformBulkRequestsUri}`, payload);
  }

  createBulkRequests(payload) {
    return this.jwtBaseClient.post(`${this.createBulkRequestsUri}`, payload);
  }

  getTransportationRequests(
    page,
    size,
    sort,
    search,
    statuses,
    includeServiceType,
  ) {
    const payload = {
      statuses,
      search,
      includeServiceType,
    };

    const query = `page=${
      page}&size=${size}&sort=${sort || 'createdDate,desc'}`;

    return this.jwtBaseClient.post(
      `${this.searchRequestUri}?${query}`,
      payload,
      null,
      null,
      1,
    );
  }

  getPromotions() {
    return this.jwtBaseClient.get(`${this.transportPromoUri}`);
  }

  getTransportContactByDealer(dealerId) {
    return this.jwtBaseClient.get(`/api/v1/dealer/${dealerId}/transport-contact`);
  }

  /**
  *
  * @param {Object} payload - The request payload.
  * @param {Array<Object>} payload.vehicles - List of vehicle objects.
  * @param {string} payload.vehicles[].vin - The Vehicle Identification Number (VIN).
  * @param {boolean} payload.vehicles[].inop - Indicates if the vehicle is inoperable (optional).
  * @param {string} payload.pickUpZip - The pickup ZIP code (optional).
  * @param {string} [payload.deliveryZip] - The delivery ZIP code (optional).
  *
  * @returns {Promise} a list of vindecoded responses with applicable service type for each vin
  */
  getCheckoutOptions(payload) {
    return this.jwtBaseClient.post(`${this.checkoutOptions}`, payload);
  }

  /**
  *
  * @param {Object} payload - The request payload.
  * @param {Array<Object>} payload.vehicles - List of vehicle objects.
  * @param {string} payload.dealerId - The ID of current user's Dealer.
  * @param {string} payload.userMemail - The email of current user.
  * @param {string} payload.serviceType - type of delivery quote, currently 'enclosed' or 'open'.
  * @param {string} payload.pickUpZip - The pickup ZIP code.
  * @param {string} payload.deliveryZip - The delivery ZIP code.
  *
  * @returns transport quotes for vins provided based on zips and service types
  */
  getTransportQuote(payload) {
    return this.jwtBaseClient.post(`${this.transportQuote}`, payload);
  }

  /**
   *
   * @param {*} payload
   * @param {*} acvUserId
   * @returns transport request details and possible errors
   */
  createTransportRequest(payload, acvUserId) {
    return this.jwtBaseClient.post(`${this.createTransportRequest}`, payload, { 'acv-user-id': acvUserId });
  }

  /**
   * Retrieves transport quotes based on the provided payload and query parameters.
   *
   * @param {Object} payload - The request body containing the data for the quote search.
   * @param {string} payload.auctionId - The auction ID.
   * @param {string} payload.dealerId - The ID of current user's Dealer.
   * @param {string} [payload.addressId] - The ID of the address (optional).
   * @param {Boolean} payload.includeServiceType - flag if returned quotes include servicetype
   * @param {Object} queryParams - The query parameters for pagination and sorting.
   * @param {number} [queryParams.page] - The page number for pagination.
   * @param {number} [queryParams.size] - The number of items per page.
   * @param {string} [queryParams.sort] - The sorting criteria.
   * @returns returns either a paginated list of quotes or one quote if an address id is provided
   */
  getQuotes(payload, acvUserId) {
    return this.jwtBaseClient.post(`${this.transportQuoteAuction}`, payload, { 'acv-user-id': acvUserId });
  }
}
export default TransportGatewayClient;
